// 2026-02 · Modal de biblioteca de recetas (Fit Evolution + 1 a 1).
// Paginación REAL (page/limit) + scroll infinito. Carga 20 recetas al abrir
// y pide la próxima página sólo cuando el usuario llega al final.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FaTimes, FaSearch, FaFire, FaClock, FaCheck } from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const SLOT_META = {
  'Desayuno':    { emoji: '🍳', color: 'text-amber-300' },
  'Snack AM':    { emoji: '🍎', color: 'text-emerald-300' },
  'Almuerzo':    { emoji: '🍽️', color: 'text-orange-300' },
  'Snack PM':    { emoji: '🥜', color: 'text-lime-300' },
  'Cena':        { emoji: '🌙', color: 'text-purple-300' },
  'Post-Entreno':{ emoji: '💪', color: 'text-cyan-300' },
};
const CANONICAL_SLOTS = Object.keys(SLOT_META);

function _slotForTime(mealTime) {
  try {
    if (!mealTime) return 'Todas';
    const h = parseInt(String(mealTime).split(':')[0], 10);
    if (Number.isFinite(h)) {
      if (h < 10) return 'Desayuno';
      if (h < 12) return 'Snack AM';
      if (h < 15) return 'Almuerzo';
      if (h < 18) return 'Snack PM';
      return 'Cena';
    }
  } catch { /* noop */ }
  return 'Todas';
}

export default function RecipeLibraryModal({ isOpen, onClose, mealIndex, mealTime, dayType, onAdded, mode = 'add', batchId, targetRecipeId }) {
  const [items, setItems] = useState([]);
  const [slotCounts, setSlotCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeSlot, setActiveSlot] = useState('Todas');
  const [addingId, setAddingId] = useState(null);
  // 2026-02 · Estado "cerrar déficit": se activa cuando la receta recién
  // agregada/reemplazada quedó fuera del ±10% en algún macro.
  const [closeMacro, setCloseMacro] = useState(null);  // { macro, deficit_g, context, recipeName, options:[], chosenFood, loading, submitting }

  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const requestIdRef = useRef(0);

  // Debounce de 350 ms sobre la búsqueda para no fatigar al backend.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Slot inicial sugerido por la hora de la comida (si está disponible).
  useEffect(() => {
    if (isOpen) setActiveSlot(_slotForTime(mealTime));
  }, [isOpen, mealTime]);

  const fetchPage = useCallback(async (targetPage, { append = false } = {}) => {
    const reqId = ++requestIdRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const params = { page: targetPage, limit: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      if (activeSlot && activeSlot !== 'Todas') params.meal_slot = activeSlot;
      const { data } = await axios.get(`${API}/user/recipe-library/for-meal`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params,
      });
      if (reqId !== requestIdRef.current) return; // response obsoleta
      const newItems = data?.items || [];
      setItems((prev) => append ? [...prev, ...newItems] : newItems);
      setSlotCounts(data?.slot_counts || {});
      setTotal(Number(data?.total) || 0);
      setPage(Number(data?.page) || targetPage);
      setHasMore(!!data?.has_more);
    } catch (e) {
      if (!append) setItems([]);
      toast.error(e?.response?.data?.detail || 'Error cargando la biblioteca');
    } finally {
      if (reqId === requestIdRef.current) {
        if (append) setLoadingMore(false); else setLoading(false);
      }
    }
  }, [debouncedSearch, activeSlot]);

  // Reset + fetch página 1 al abrir, cambiar tab o cambiar búsqueda.
  useEffect(() => {
    if (!isOpen) return;
    setItems([]);
    setPage(1);
    setHasMore(false);
    fetchPage(1, { append: false });
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [isOpen, activeSlot, debouncedSearch, fetchPage]);

  // Scroll infinito via IntersectionObserver sobre un sentinel al pie.
  useEffect(() => {
    if (!isOpen || !hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const root = scrollContainerRef.current || null;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !loadingMore && hasMore) {
          fetchPage(page + 1, { append: true });
          break;
        }
      }
    }, { root, rootMargin: '220px 0px', threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [isOpen, hasMore, loadingMore, page, fetchPage]);

  const _macroHuman = (k) => ({ protein: 'proteína', carbs: 'carbos', fats: 'grasa' }[k] || k);

  // Dado el macro_diagnostics del backend, detecta el primer macro con déficit
  // real (scaled < target y fuera de tolerancia). Si no hay déficit, retorna null.
  const _detectDeficit = (diag) => {
    if (!diag || typeof diag !== 'object') return null;
    // Ticket 280983: con las calorias ya por encima de la meta no se ofrece agregar
    // alimentos, y se elige el macro con mayor deficit relativo.
    const c = diag.calories;
    if (c && c.within_tolerance === false && Number(c.scaled) > Number(c.target)) return null;
    let best = null;
    for (const k of ['protein', 'carbs', 'fats']) {
      const d = diag[k];
      if (d && d.within_tolerance === false && Number(d.scaled) < Number(d.target)) {
        const gap = Number(d.target) - Number(d.scaled);
        const rel = Number(d.target) > 0 ? gap / Number(d.target) : 0;
        if (!best || rel > best.rel) best = { macro: k, deficit_g: Math.max(0.1, gap), rel };
      }
    }
    return best ? { macro: best.macro, deficit_g: best.deficit_g } : null;
  };

  const _openCloseMacroPanel = async (data, recipeName) => {
    const deficit = _detectDeficit(data?.macro_diagnostics);
    if (!deficit || !data?.close_macro_context) return false;
    setCloseMacro({
      macro: deficit.macro,
      deficit_g: deficit.deficit_g,
      context: data.close_macro_context,
      recipeName,
      options: [],
      chosenFood: '',
      loading: true,
      submitting: false,
    });
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data: fd } = await axios.get(`${API}/user/recipe-close-macro/foods`, {
        headers,
        params: { macro: deficit.macro, limit: 500 },
      });
      setCloseMacro(prev => prev ? { ...prev, options: fd?.options || [], loading: false } : prev);
    } catch (e) {
      setCloseMacro(prev => prev ? { ...prev, loading: false } : prev);
      toast.error('No se pudo cargar la lista de alimentos');
    }
    return true;
  };

  const handleCloseMacroSubmit = async () => {
    if (!closeMacro || !closeMacro.chosenFood) {
      toast.error('Elegí un alimento primero');
      return;
    }
    setCloseMacro(prev => prev ? { ...prev, submitting: true } : prev);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const body = {
        scope: closeMacro.context.scope,
        macro: closeMacro.macro,
        deficit_g: closeMacro.deficit_g,
        food_name: closeMacro.chosenFood,
        ...closeMacro.context,
      };
      const { data } = await axios.post(`${API}/user/recipe-close-macro/add-ingredient`, body, { headers });
      toast.success(`Se agregaron ${data.added_grams}g de ${closeMacro.chosenFood} para cerrar el déficit de ${_macroHuman(closeMacro.macro)}`);
      setCloseMacro(null);
      onAdded?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo sumar el alimento');
      setCloseMacro(prev => prev ? { ...prev, submitting: false } : prev);
    }
  };

  const handleAdd = async (recipe) => {
    setAddingId(recipe.id);
    try {
      const token = localStorage.getItem('deisycfit_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (mode === 'replace') {
        if (!batchId || !targetRecipeId) {
          toast.error('Falta información para reemplazar (batchId/targetRecipeId)');
          return;
        }
        const { data } = await axios.post(
          `${API}/user/fit-evolution/replace-recipe-from-library`,
          { batch_id: batchId, recipe_id: targetRecipeId, library_recipe_id: recipe.id },
          { headers }
        );
        if (data.macro_within_tolerance === false) {
          const opened = await _openCloseMacroPanel(data, data.new_name || recipe.name);
          if (opened) return;
          toast(`Receta reemplazada por "${data.new_name || recipe.name}", pero sus macros no quedaron dentro del ±10% del objetivo${data.macro_note ? `: ${data.macro_note}` : ''}.`, { icon: '⚠️', duration: 6000 });
        } else {
          toast.success(`Receta reemplazada por "${data.new_name || recipe.name}"`);
        }
        onAdded?.(data);
        onClose?.();
        return;
      }
      const { data } = await axios.post(
        `${API}/user/meal-plan/add-recipe-option`,
        { recipe_id: recipe.id, meal_index: mealIndex, day_type: dayType },
        { headers }
      );
      if (data.macro_within_tolerance === false) {
        const opened = await _openCloseMacroPanel(data, recipe.name);
        if (opened) return;
      }
      toast.success(`"${recipe.name}" agregada como opción ${((data.option_index ?? 0) + 1)}`);
      onAdded?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (mode === 'replace' ? 'No se pudo reemplazar la receta' : 'No se pudo agregar la receta'));
    } finally {
      setAddingId(null);
    }
  };

  if (!isOpen) return null;

  // Chips: "Todas" + los 6 slots canónicos, pintando el count de slot_counts.
  const totalAcrossSlots = Object.values(slotCounts).reduce((a, b) => a + (Number(b) || 0), 0);
  const chips = [
    { slot: 'Todas', count: totalAcrossSlots || total, emoji: '🍴' },
    ...CANONICAL_SLOTS.map((s) => ({ slot: s, count: slotCounts[s] || 0, emoji: SLOT_META[s].emoji })),
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="recipe-library-modal">
      <div className="bg-gray-950 border border-pink-500/40 rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-pink-500/30 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">📚 Biblioteca de recetas</h3>
            <p className="text-xs text-gray-400 mt-1">
              {mode === 'replace'
                ? 'Elegí una receta: reemplazará la actual de esta comida (se escalará a tus macros).'
                : `Comida #${mealIndex + 1} · Ajustadas a tus macros`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="close-recipe-library">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto" ref={scrollContainerRef} data-testid="recipe-library-scroll">
          <div className="relative mb-3">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o ingrediente (ej: pollo, papa)…"
              className="bg-gray-900 border-gray-700 text-white pl-9"
              data-testid="recipe-library-search"
            />
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="recipe-library-tabs">
            {chips.map(({ slot, count, emoji }) => {
              const isActive = activeSlot === slot;
              const disabled = slot !== 'Todas' && count === 0;
              return (
                <button
                  key={slot}
                  onClick={() => !disabled && setActiveSlot(slot)}
                  disabled={disabled}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    isActive ? 'bg-pink-500 text-white' : disabled ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`slot-tab-${slot.replace(/\s+/g,'-')}`}
                >
                  {emoji} {slot} {count > 0 && <span className="opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>

          {loading && items.length === 0 && (
            <p className="text-center text-gray-400 py-10 text-sm">Cargando recetas…</p>
          )}

          {!loading && items.length === 0 && (
            <p className="text-center text-gray-500 py-10 text-sm">
              {debouncedSearch ? `Sin resultados para "${debouncedSearch}".` : 'No hay recetas en esta categoría.'}
            </p>
          )}

          {items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recipe-library-list">
              {items.map((r) => (
                <div key={r.id} className="p-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition" data-testid={`recipe-card-${r.id}`}>
                  <div className="flex items-start gap-3">
                    {r.image_url || r.image_base64 ? (
                      <img src={r.image_url || r.image_base64} alt={r.name} loading="lazy" decoding="async" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-gray-800 flex-shrink-0 flex items-center justify-center text-xl">
                        {SLOT_META[r.meal_slot]?.emoji || '🍽️'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{r.name}</p>
                      <div className="flex gap-2 mt-1 text-[11px] text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1"><FaFire className="text-orange-400" />{Math.round(r.calories || 0)} kcal</span>
                        {r.prep_minutes ? <span className="flex items-center gap-1"><FaClock />{r.prep_minutes}m</span> : null}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        P {r.protein || 0}g · C {r.carbs || 0}g · G {r.fats || 0}g
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAdd(r)}
                    disabled={addingId === r.id}
                    className="w-full mt-2 bg-pink-600 hover:bg-pink-700"
                    data-testid={`add-recipe-${r.id}`}
                  >
                    {addingId === r.id
                      ? (mode === 'replace' ? 'Reemplazando…' : 'Agregando…')
                      : (<><FaCheck className="mr-1.5" /> {mode === 'replace' ? 'Reemplazar esta comida' : 'Agregar como opción'}</>)
                    }
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Sentinel para scroll infinito. Sólo visible mientras queden páginas. */}
          {hasMore && (
            <div ref={sentinelRef} data-testid="recipe-library-sentinel" className="py-4 text-center text-xs text-gray-500">
              {loadingMore ? 'Cargando más recetas…' : `Mostrando ${items.length}/${total}`}
            </div>
          )}
          {!hasMore && items.length > 0 && (
            <div className="py-4 text-center text-[11px] text-gray-600">— {items.length}/{total} recetas —</div>
          )}
        </div>

        {/* 2026-02 · Panel "cerrar déficit" */}
        {closeMacro && (
          <div className="border-t border-amber-500/40 bg-amber-500/10 p-4 space-y-3" data-testid="close-macro-panel">
            <div>
              <p className="text-sm font-bold text-amber-200">
                ⚠️ Excelente opción, pero "{closeMacro.recipeName}" es baja en {_macroHuman(closeMacro.macro)} para cumplir tu meta.
              </p>
              <p className="text-xs text-amber-100/80 mt-1">
                Faltan <span className="font-bold">{Math.round(closeMacro.deficit_g * 10) / 10} g</span> de {_macroHuman(closeMacro.macro)}. Elegí un alimento de tu Gestor de Alimentos y lo sumo con los gramos exactos.
              </p>
            </div>
            {closeMacro.loading ? (
              <p className="text-xs text-gray-300">Cargando alimentos…</p>
            ) : (
              <>
                <select
                  value={closeMacro.chosenFood}
                  onChange={(e) => setCloseMacro(prev => prev ? { ...prev, chosenFood: e.target.value } : prev)}
                  className="w-full bg-gray-900 border border-amber-400/40 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="close-macro-food-select"
                >
                  <option value="">— Seleccionar alimento —</option>
                  {closeMacro.options.map(o => (
                    <option key={o.name} value={o.name}>
                      {o.is_user_food ? '⭐ ' : ''}{o.name} · {o.per_100g.toFixed(1)} g {_macroHuman(closeMacro.macro)}/100g
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCloseMacroSubmit}
                    disabled={!closeMacro.chosenFood || closeMacro.submitting}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold"
                    data-testid="close-macro-submit"
                  >
                    {closeMacro.submitting ? 'Sumando…' : 'Sumar a la receta y actualizar totales'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setCloseMacro(null); onAdded?.({ skipped_close_macro: true }); onClose?.(); }}
                    data-testid="close-macro-skip"
                  >
                    Omitir
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
